import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { CharacterId } from '@/types'
import { getXpForLevel, getLevelFromXp } from '@/types'
import { checkMilestoneCrossed, MILESTONES, type MilestonePercent } from '@/lib/milestones'
import { queueSync } from '@/lib/sync'

export interface XpGainResult {
  leveledUp: boolean
  newLevel: number
  milestoneCrossed: MilestonePercent | null
  gemReward: number
  affectionBonus: number
}

interface UserState {
  // User data
  id: string | null
  username: string | null
  level: number
  xp: number
  xpInCurrentLevel: number
  xpForNextLevel: number
  gems: number
  streakDays: number
  lastActiveDate: string | null
  activeCharacterId: CharacterId
  isAuthenticated: boolean
  lastMilestone: MilestonePercent | null

  // Actions
  setUser: (user: Partial<UserState>) => void
  addXp: (amount: number) => XpGainResult
  addGems: (amount: number) => void
  spendGems: (amount: number) => boolean
  setActiveCharacter: (id: CharacterId) => void
  updateStreak: () => void
  logout: () => void
}

const initialState = {
  id: null,
  username: null,
  level: 1,
  xp: 0,
  xpInCurrentLevel: 0,
  xpForNextLevel: getXpForLevel(1),
  gems: 100,
  streakDays: 0,
  lastActiveDate: null,
  activeCharacterId: 'hana' as CharacterId,
  isAuthenticated: false,
  lastMilestone: null as MilestonePercent | null,
}

export const useUserStore = create<UserState>()(
  persist(
    (set, get) => ({
      ...initialState,

      setUser: (user) => set((state) => ({ ...state, ...user })),

      addXp: (amount) => {
        const state = get()
        const previousXpInLevel = state.xpInCurrentLevel
        const totalXp = state.xp + amount
        const { level, currentXp, xpForNextLevel } = getLevelFromXp(totalXp)
        const leveledUp = level > state.level

        // Check for milestone within current level (before level up)
        let milestoneCrossed: MilestonePercent | null = null
        let gemReward = 0
        let affectionBonus = 0

        if (leveledUp) {
          // Level up is the ultimate milestone
          milestoneCrossed = 1.0
          gemReward = MILESTONES[1.0].gemReward
          affectionBonus = MILESTONES[1.0].affectionBonus
        } else {
          // Check for interim milestones
          milestoneCrossed = checkMilestoneCrossed(
            previousXpInLevel,
            currentXp,
            state.xpForNextLevel
          )
          if (milestoneCrossed && milestoneCrossed < 1.0) {
            gemReward = MILESTONES[milestoneCrossed].gemReward
            affectionBonus = MILESTONES[milestoneCrossed].affectionBonus
          }
        }

        // Apply gem reward if milestone was crossed
        const newGems = state.gems + gemReward

        set({
          xp: totalXp,
          level,
          xpInCurrentLevel: currentXp,
          xpForNextLevel,
          gems: newGems,
          lastMilestone: milestoneCrossed,
        })

        // Queue profile sync
        if (state.id) {
          queueSync('profile', 'update', state.id, {
            level,
            xp: totalXp,
            gems: newGems,
          })
        }

        return { leveledUp, newLevel: level, milestoneCrossed, gemReward, affectionBonus }
      },

      addGems: (amount) => {
        const state = get()
        const newGems = state.gems + amount
        set({ gems: newGems })

        // Queue profile sync
        if (state.id) {
          queueSync('profile', 'update', state.id, { gems: newGems })
        }
      },

      spendGems: (amount) => {
        const state = get()
        if (state.gems >= amount) {
          const newGems = state.gems - amount
          set({ gems: newGems })

          // Queue profile sync
          if (state.id) {
            queueSync('profile', 'update', state.id, { gems: newGems })
          }
          return true
        }
        return false
      },

      setActiveCharacter: (id) => {
        const state = get()
        set({ activeCharacterId: id })

        // Queue profile sync
        if (state.id) {
          queueSync('profile', 'update', state.id, { active_character_id: id })
        }
      },

      updateStreak: () => {
        const state = get()
        const today = new Date().toISOString().split('T')[0]
        const yesterday = new Date(Date.now() - 86400000).toISOString().split('T')[0]

        if (state.lastActiveDate === today) {
          // Already updated today
          return
        }

        if (state.lastActiveDate === yesterday) {
          // Continuing streak
          set({
            streakDays: state.streakDays + 1,
            lastActiveDate: today,
          })
        } else {
          // Streak broken, start fresh
          set({
            streakDays: 1,
            lastActiveDate: today,
          })
        }
      },

      logout: () => set(initialState),
    }),
    {
      name: 'waifudo-user',
    }
  )
)
